<template>
    <div class="emp-page">
        <!-- Header Toolbar -->
        <div class="emp-toolbar">
            <div class="emp-toolbar__left">
                <h1 class="emp-toolbar__title">
                    <i class="fas fa-users emp-toolbar__icon"></i>
                    Employees
                </h1>
                <span class="emp-toolbar__badge" v-if="totalRecords">{{ totalRecords }}</span>
            </div>
            <div class="emp-toolbar__actions">
                <AllowPermission :permission="PERMISSION_CODES.EDIT_EMPLOYEES">
                    <button class="emp-btn emp-btn--outline" @click="updateEmployeeCTC" :disabled="updatingEmployeeCTC">
                        <i class="fas fa-sync-alt" :class="{ 'fa-spin': updatingEmployeeCTC }"></i>
                        {{ updatingEmployeeCTC ? "Syncing..." : "Sync CTCs" }}
                    </button>
                </AllowPermission>
                <AllowPermission :permission="PERMISSION_CODES.CREATE_EMPLOYEES">
                    <button class="emp-btn emp-btn--primary" @click="navigateToCreateEmployee">
                        <i class="fas fa-plus"></i>
                        Add Employee
                    </button>
                </AllowPermission>
            </div>
        </div>

        <!-- Filter Bar -->
        <div class="emp-filters">
            <div class="emp-filter-group">
                <div class="emp-filter-item" v-if="companies.length">
                    <label class="emp-filter-label">
                        <i class="fas fa-building"></i> Company
                    </label>
                    <SelectDropdown
                        :id="`form-subject`"
                        :options="companies.map((item) => ({ id: item.id, title: item.name }))"
                        :value="selectedCompanyId"
                        @change="selectedCompanyId = $event"
                    />
                </div>
                <div class="emp-filter-item" v-if="userRoles.length">
                    <label class="emp-filter-label">
                        <i class="fas fa-user-tag"></i> Role
                    </label>
                    <SelectDropdown
                        :id="`form-user-role`"
                        :options="userRoles.filter((item) => !item.name.includes('admin')).map((item) => ({ id: item.id, title: item.name }))"
                        :value="selectedUserRoleId"
                        @change="selectedUserRoleId = $event"
                    />
                </div>
            </div>
            <div class="emp-search-wrap">
                <i class="fas fa-search emp-search-icon"></i>
                <input
                    v-model="searchQuery"
                    class="emp-search-input"
                    placeholder="Search by name, email..."
                    type="text"
                />
                <button v-if="searchQuery" class="emp-search-clear" @click="searchQuery = ''">
                    <i class="fas fa-times"></i>
                </button>
            </div>
        </div>

        <!-- Content Card -->
        <AllowPermission :permission="PERMISSION_CODES.VIEW_EMPLOYEES">
            <div class="emp-card">
                <div v-if="loading" class="emp-loading">
                    <Spinner />
                    <span>Loading employees...</span>
                </div>
                <div v-else-if="employees.length > 0">
                    <div class="emp-table-wrap">
                        <table class="emp-table">
                            <thead>
                                <tr>
                                    <th class="emp-th-num">#</th>
                                    <th>Full Name</th>
                                    <th>CTC</th>
                                    <th>Email</th>
                                    <th>DOC Updated</th>
                                    <th class="emp-th-actions">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(employee, idx) of employees" :key="employee.id">
                                    <td class="emp-td-num">{{ rangeStart + idx }}</td>
                                    <td>
                                        <div class="emp-name-cell">
                                            <div class="emp-avatar">
                                                {{ getInitials(employee) }}
                                            </div>
                                            <div>
                                                <div class="emp-name">{{ getDisplayName(employee) }}</div>
                                                <div class="emp-name-sub" v-if="employee.designation">{{ employee.designation }}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span class="emp-ctc" v-if="employee.ctc">₹{{ Number(employee.ctc).toLocaleString('en-IN') }}</span>
                                        <span class="emp-ctc emp-ctc--na" v-else>—</span>
                                    </td>
                                    <td>
                                        <span class="emp-email">{{ employee.email || '—' }}</span>
                                    </td>
                                    <td>
                                        <span
                                            class="emp-doc-badge"
                                            :class="employee.docCompleted ? 'emp-doc-badge--yes' : 'emp-doc-badge--no'"
                                        >
                                            {{ employee.docCompleted ? 'Yes' : 'No' }}
                                        </span>
                                    </td>
                                    <td>
                                        <div class="emp-actions">
                                            <AllowPermission :permission="PERMISSION_CODES.EDIT_EMPLOYEES">
                                                <a
                                                    class="emp-action-btn emp-action-btn--edit"
                                                    :href="navigatePage({ name: 'employee-edit', params: { id: employee.id } }, true)"
                                                    title="Edit Employee"
                                                >
                                                    <i class="fas fa-pen"></i>
                                                </a>
                                            </AllowPermission>
                                            <AllowPermission :permission="PERMISSION_CODES.DELETE_EMPLOYEES">
                                                <button
                                                    class="emp-action-btn emp-action-btn--delete"
                                                    @click="deleteDataItem(employee.id)"
                                                    title="Delete Employee"
                                                >
                                                    <i class="fas fa-trash-alt"></i>
                                                </button>
                                            </AllowPermission>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination Bar -->
                    <div class="emp-pagination-bar" v-if="employees.length">
                        <div class="emp-page-info">
                            <span class="emp-page-info-text">
                                Showing <strong>{{ rangeStart }}–{{ rangeEnd }}</strong> of <strong>{{ totalRecords }}</strong>
                            </span>
                            <div class="emp-per-page">
                                <label>Per page</label>
                                <select v-model.number="limit" @change="onLimitChange" class="emp-per-page-select">
                                    <option :value="10">10</option>
                                    <option :value="25">25</option>
                                    <option :value="50">50</option>
                                    <option :value="100">100</option>
                                </select>
                            </div>
                        </div>
                        <div class="emp-page-nav" v-if="totalPages > 1">
                            <button class="emp-pg-btn" :disabled="page <= 1" @click="goPage(1)" title="First">
                                <i class="fas fa-angle-double-left"></i>
                            </button>
                            <button class="emp-pg-btn" :disabled="page <= 1" @click="goPage(page - 1)" title="Previous">
                                <i class="fas fa-angle-left"></i>
                            </button>
                            <template v-for="(p, pIdx) in visiblePages">
                                <span v-if="p === '...'" :key="'ellipsis-'+pIdx" class="emp-pg-ellipsis">...</span>
                                <button v-else :key="'pg-'+p" class="emp-pg-btn" :class="{ 'emp-pg-btn--active': p === page }" @click="goPage(p)">
                                    {{ p }}
                                </button>
                            </template>
                            <button class="emp-pg-btn" :disabled="page >= totalPages" @click="goPage(page + 1)" title="Next">
                                <i class="fas fa-angle-right"></i>
                            </button>
                            <button class="emp-pg-btn" :disabled="page >= totalPages" @click="goPage(totalPages)" title="Last">
                                <i class="fas fa-angle-double-right"></i>
                            </button>
                        </div>
                    </div>
                </div>
                <div v-else class="emp-empty">
                    <i class="fas fa-user-slash emp-empty__icon"></i>
                    <p class="emp-empty__text">No employees found</p>
                    <p class="emp-empty__sub">Try adjusting your filters or search query</p>
                </div>
            </div>
        </AllowPermission>
    </div>
</template>

<script>
import { http } from "@/utils/http"
import CrudIndex from "@/components/crud/index.vue"
import { createField, createColumn } from "@/utils/common"
import Button from "../../components/UI/Button.vue"
import { errMsg } from "../../utils/common"
import SelectDropdown from "../../components/crud/SelectDropdown.vue"
import { mapState } from "vuex"
import { PERMISSION_CODES } from "../../store/permission"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import PageCard from "../../components/layout/PageCard.vue"
import SectionTitle from "../../components/UI/SectionTitle.vue"
import CustomInput from "../../components/crud/custom-input.vue"
import CustomTable from "../../components/other/CustomTable.vue"
import Spinner from "../../components/UI/Spinner.vue"

export default {
    data() {
        return {
            PERMISSION_CODES,
            showResults: true,
            loading: false,
            videos: [],
            limit: 10,
            page: 1,
            totalPages: 0,
            totalRecords: 0,
            createFields: [],
            selectedCompanyId: null,
            selectedUserRoleId: null,
            updatingEmployeeCTC: false,
            employees: [],
            searchQuery: "",
            columns: [
                createColumn("Full Name", "fullName", "string"),
                createColumn("CTC", "ctc", "number"),
                createColumn("Email", "email", "string"),
                createColumn("Doc Updated", "docCompleted", "string"),
            ],
        }
    },

    computed: {
        ...mapState("misc", ["companies", "userRoles"]),
        rangeStart() {
            return this.totalRecords === 0 ? 0 : (this.page - 1) * this.limit + 1
        },
        rangeEnd() {
            return Math.min(this.page * this.limit, this.totalRecords)
        },
        visiblePages() {
            const total = this.totalPages
            const current = this.page
            if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
            const pages = []
            pages.push(1)
            if (current > 3) pages.push("...")
            const start = Math.max(2, current - 1)
            const end = Math.min(total - 1, current + 1)
            for (let i = start; i <= end; i++) pages.push(i)
            if (current < total - 2) pages.push("...")
            pages.push(total)
            return pages
        },
    },
    props: { videoId: { type: String, default: "" } },
    components: {
        Spinner,
        CustomTable,
        CustomInput,
        SectionTitle,
        PageCard,
        AllowPermission,
        CrudIndex,
        Button,
        SelectDropdown,
    },
    async mounted() {
        this.fetchEmployees()
    },
    methods: {
        getDisplayName(employee) {
            if (employee.fullName) return employee.fullName
            const parts = [employee.firstName, employee.lastName].filter(Boolean)
            return parts.length ? parts.join(' ') : (employee.name || employee.email || '—')
        },
        getInitials(employee) {
            const name = this.getDisplayName(employee)
            if (!name || name === '—') return '?'
            return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
        },
        goPage(p) {
            if (p < 1 || p > this.totalPages || p === this.page) return
            this.page = p
            window.scrollTo({ top: 0, behavior: 'smooth' })
            this.fetchEmployees()
        },
        onLimitChange() {
            this.page = 1
            this.fetchEmployees()
        },
        async handlePageChange(newPage) {
            if (this.page != newPage) {
                this.page = newPage
                this.fetchEmployees()
            }
        },
        navigatePage(routeObj, onlyUrl = false) {
            const url = this.$router.resolve(routeObj).href
            if (onlyUrl) return url
            window.location.href = url
        },
        async deleteDataItem(id) {
            try {
                if (confirm("Do you really want to delete?")) {
                    await http.delete(`delete/${id}`)
                    this.$toast.success(this.title + " Deleted Successfully!")
                    this.employees = this.employees.filter((item) => item.id !== id)
                    window.location.reload()
                }
            } catch (err) {
                console.log("error in deleting " + this.title + " item", err)
                this.$toast.error("Some error occured")
            }
        },
        async fetchEmployees() {
            try {
                this.loading = true
                const res = await http.get(
                    `/employees?company_id=${this.selectedCompanyId || ""}&role_id=${this.selectedUserRoleId || ""}&search=${
                        this.searchQuery
                    }&page=${this.page}&limit=${this.limit}`
                )
                this.employees = res.data.result
                this.totalPages = res.data.totalPages
                this.totalRecords = res.data.total || (res.data.totalPages * this.limit) || 0
            } catch (err) {
                console.log("error in fetching employees", err)
            }
            this.loading = false
        },
        navigateToCreateEmployee() {
            this.$router.push("/employees/create")
        },
        async updateEmployeeCTC() {
            this.updatingEmployeeCTC = true
            try {
                const res = await http.patch("/payroll-payments/employee-cts")
                this.$toast.success("Updated employee cts.")
                window.location.reload()
            } catch (err) {
                console.log("err", err)
                this.$toast.error(errMsg(err, "Error occured"))
            }
            this.updatingEmployeeCTC = false
        },
    },
    watch: {
        searchQuery(e) {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.page = 1
                this.fetchEmployees()
            }, 800)
        },
        selectedCompanyId() {
            this.page = 1
            this.fetchEmployees()
        },
        selectedUserRoleId() {
            this.page = 1
            this.fetchEmployees()
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

/* ── Page Layout ── */
.emp-page {
    padding: 0;
}

/* ── Toolbar ── */
.emp-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
}
.emp-toolbar__left {
    display: flex;
    align-items: center;
    gap: 10px;
}
.emp-toolbar__title {
    font-size: 22px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;
}
.emp-toolbar__icon {
    color: $primaryColor;
    font-size: 20px;
}
.emp-toolbar__badge {
    background: $primaryLight;
    color: $primaryColor;
    font-size: 12px;
    font-weight: 600;
    padding: 2px 10px;
    border-radius: 20px;
}
.emp-toolbar__actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

/* ── Buttons ── */
.emp-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 9px 18px;
    border-radius: $radiusSm;
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    border: none;
    transition: all $transitionFast;

    &--primary {
        background: $primaryColor;
        color: white;

        &:hover { background: $primaryDark; }
    }
    &--outline {
        background: white;
        color: $primaryColor;
        border: 1.5px solid $primaryColor;

        &:hover {
            background: $primaryLight;
        }
        &:disabled {
            opacity: 0.55;
            cursor: not-allowed;
        }
    }
}

/* ── Filter Bar ── */
.emp-filters {
    display: flex;
    align-items: flex-end;
    gap: 16px;
    padding: 16px 20px;
    background: white;
    border: 1px solid $gray100;
    border-radius: $radiusMd;
    margin-bottom: 20px;
    flex-wrap: wrap;
    box-shadow: $shadowSm;
}
.emp-filter-group {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    flex: 1;
    min-width: 0;
}
.emp-filter-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 180px;
    max-width: 240px;
    flex: 1;
}
.emp-filter-label {
    font-size: 11px;
    font-weight: 600;
    color: $gray500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    gap: 5px;

    i {
        font-size: 10px;
        color: $gray400;
    }
}

/* ── Search ── */
.emp-search-wrap {
    position: relative;
    min-width: 220px;
    max-width: 300px;
    flex-shrink: 0;
}
.emp-search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: $gray400;
    font-size: 13px;
    pointer-events: none;
}
.emp-search-input {
    width: 100%;
    padding: 9px 34px 9px 34px;
    border: 1.5px solid $gray200;
    border-radius: $radiusSm;
    font-size: 13px;
    color: $gray700;
    background: $gray50;
    font-family: inherit;
    transition: border-color $transitionFast, box-shadow $transitionFast;
    outline: none;

    &::placeholder { color: $gray400; }
    &:focus {
        border-color: $primaryColor;
        background: white;
        box-shadow: 0 0 0 3px rgba($primaryColor, 0.08);
    }
}
.emp-search-clear {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: $gray400;
    cursor: pointer;
    padding: 2px 4px;
    font-size: 12px;

    &:hover { color: $gray600; }
}

/* ── Content Card ── */
.emp-card {
    background: white;
    border: 1px solid $gray100;
    border-radius: $radiusMd;
    box-shadow: $shadowSm;
    overflow: hidden;
}

/* ── Loading ── */
.emp-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 20px;
    gap: 12px;
    color: $gray500;
    font-size: 13px;
}

/* ── Table ── */
.emp-table-wrap {
    overflow-x: auto;
}
.emp-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    thead {
        background: $gray50;
        border-bottom: 2px solid $gray100;
    }
    th {
        padding: 12px 16px;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: $gray500;
        text-align: left;
        white-space: nowrap;
    }
    tbody tr {
        border-bottom: 1px solid $gray100;
        transition: background $transitionFast;

        &:hover { background: $gray50; }
        &:last-child { border-bottom: none; }
    }
    td {
        padding: 12px 16px;
        color: $gray700;
        vertical-align: middle;
    }
}
.emp-th-num { width: 50px; }
.emp-td-num {
    font-size: 12px;
    color: $gray400;
    font-weight: 500;
}
.emp-th-actions { width: 100px; text-align: center; }

/* ── Name Cell ── */
.emp-name-cell {
    display: flex;
    align-items: center;
    gap: 10px;
}
.emp-avatar {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: linear-gradient(135deg, $primaryLight, lighten($primaryColor, 30%));
    color: $primaryDark;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.emp-name {
    font-weight: 600;
    color: $gray800;
    font-size: 13px;
}
.emp-name-sub {
    font-size: 11px;
    color: $gray400;
    margin-top: 1px;
}

/* ── CTC ── */
.emp-ctc {
    font-weight: 600;
    color: $gray700;
    font-size: 13px;

    &--na {
        color: $gray400;
        font-weight: 400;
    }
}

/* ── Email ── */
.emp-email {
    color: $gray600;
    font-size: 13px;
}

/* ── Doc Badge ── */
.emp-doc-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;

    &--yes {
        background: lighten($success, 42%);
        color: darken($success, 8%);
    }
    &--no {
        background: lighten($danger, 28%);
        color: darken($danger, 6%);
    }
}

/* ── Action Buttons ── */
.emp-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}
.emp-action-btn {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: $radiusSm;
    border: 1px solid transparent;
    background: transparent;
    cursor: pointer;
    font-size: 13px;
    transition: all $transitionFast;
    text-decoration: none;

    &--edit {
        color: $primaryColor;

        &:hover {
            background: $primaryLight;
            border-color: lighten($primaryColor, 30%);
        }
    }
    &--delete {
        color: $danger;

        &:hover {
            background: lighten($danger, 30%);
            border-color: lighten($danger, 20%);
        }
    }
}

/* ── Empty State ── */
.emp-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
}
.emp-empty__icon {
    font-size: 40px;
    color: $gray300;
    margin-bottom: 12px;
}
.emp-empty__text {
    font-size: 15px;
    font-weight: 600;
    color: $gray600;
    margin: 0 0 4px;
}
.emp-empty__sub {
    font-size: 13px;
    color: $gray400;
    margin: 0;
}

/* ── Pagination (existing, kept) ── */
.emp-pagination-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-top: 1px solid $gray100;
    background: $gray50;
    gap: 12px;
    flex-wrap: wrap;
}
.emp-page-info {
    display: flex;
    align-items: center;
    gap: 14px;
}
.emp-page-info-text {
    font-size: 12px;
    color: $gray500;

    strong { color: $gray700; font-weight: 600; }
}
.emp-per-page {
    display: flex;
    align-items: center;
    gap: 6px;

    label {
        font-size: 11px;
        color: $gray400;
        font-weight: 500;
    }
}
.emp-per-page-select {
    padding: 3px 6px;
    border: 1px solid $gray200;
    border-radius: 5px;
    font-size: 12px;
    color: $gray700;
    background: white;
    outline: none;
    font-family: inherit;
    cursor: pointer;

    &:focus { border-color: $primaryColor; }
}
.emp-page-nav {
    display: flex;
    align-items: center;
    gap: 3px;
}
.emp-pg-btn {
    min-width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 6px;
    border: 1px solid $gray200;
    border-radius: 6px;
    background: white;
    color: $gray600;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all $transitionFast;
    font-family: inherit;

    &:hover:not(:disabled):not(.emp-pg-btn--active) {
        background: $gray100;
        border-color: $gray300;
        color: $gray800;
    }
    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }
    &--active {
        background: $primaryColor;
        border-color: $primaryColor;
        color: white;
        font-weight: 600;
    }
}
.emp-pg-ellipsis {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    font-size: 12px;
    color: $gray400;
    letter-spacing: 1px;
}

/* ── Responsive ── */
@media (max-width: 768px) {
    .emp-toolbar { flex-direction: column; align-items: flex-start; }
    .emp-filters { flex-direction: column; align-items: stretch; }
    .emp-search-wrap { max-width: 100%; }
    .emp-filter-item { max-width: 100%; }
    .emp-pagination-bar { flex-direction: column; align-items: flex-start; padding: 10px 12px; }
    .emp-page-nav { width: 100%; justify-content: center; }
}
@media (max-width: 480px) {
    .emp-pg-btn { min-width: 28px; height: 28px; font-size: 11px; }
}
</style>
